<template>
  <div
    ref="root"
    class="transition-height duration-300 ease-in-out will-change-[height] overflow-hidden"
    style="contain: layout"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { onMounted, watch, nextTick, useTemplateRef, onUnmounted } from 'vue'

interface Props {
  show: boolean
  collapsedHeight?: number
}

const props = withDefaults(defineProps<Props>(), {
  collapsedHeight: 0,
})

const root = useTemplateRef('root')

function onTransitionEnd(e: TransitionEvent) {
  if (e.propertyName !== 'height') {
    return
  }

  if (props.show && root.value) {
    root.value.style.height = 'auto'
    root.value.style.maxHeight = 'none'
    root.value.classList.remove('overflow-hidden')
    root.value.classList.add('overflow-visible')
  }
}

function expand() {
  const el = root.value
  if (!el) {
    return
  }

  el.classList.remove('overflow-visible')
  el.classList.add('overflow-hidden')

  // Ensure we have a starting point for animation
  if (!el.style.height || el.style.height === 'auto') {
    el.style.height = `${props.collapsedHeight}px`
  }

  // force reflow
  void el.offsetHeight

  requestAnimationFrame(() => {
    const target = el.scrollHeight
    el.style.height = `${target}px`
    el.style.maxHeight = 'none'
  })
}

function collapse() {
  const el = root.value
  if (!el) {
    return
  }

  el.classList.remove('overflow-visible')
  el.classList.add('overflow-hidden')

  // Ensure we start from current height
  if (el.style.height === 'auto' || !el.style.height) {
    el.style.height = `${el.scrollHeight}px`
    el.style.maxHeight = `${el.scrollHeight}px`
  }

  // force reflow
  void el.offsetHeight

  requestAnimationFrame(() => {
    const to = props.collapsedHeight
    el.style.height = `${to}px`
    el.style.maxHeight = `${to}px`
  })
}

onMounted(() => {
  const el = root.value
  if (!el) {
    console.warn('ExpandTransition: root element not found')
    return
  }

  el.addEventListener('transitionend', onTransitionEnd)

  if (props.show) {
    el.style.height = 'auto'
    el.style.maxHeight = 'none'
    el.classList.remove('overflow-hidden')
    el.classList.add('overflow-visible')
  } else {
    el.style.height = `${props.collapsedHeight}px`
    el.style.maxHeight = `${props.collapsedHeight}px`
    el.classList.add('overflow-hidden')
  }
})

onUnmounted(() => {
  const el = root.value
  if (el) {
    el.removeEventListener('transitionend', onTransitionEnd)
  }
})

watch(
  () => props.show,
  async (val, oldVal) => {
    if (val == oldVal) {
      return
    }

    await nextTick()

    if (val) {
      expand()
    } else {
      collapse()
    }
  }
)

watch(
  () => props.collapsedHeight,
  (newHeight, oldHeight) => {
    if (newHeight === oldHeight) {
      return
    }

    const el = root.value
    if (!el) {
      return
    }

    if (!props.show) {
      el.style.height = `${newHeight}px`
    }
  }
)
</script>
